import React from 'react';
import { BarChart3, Database, MessageSquareText, Sparkles } from 'lucide-react';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const ICONS = [Database, MessageSquareText, BarChart3] as const;

const StepVisual = ({ idx, lang }: { idx: number; lang: 'en' | 'id' }) => {
  if (idx === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-center text-[10px] font-semibold text-slate-600">
            XLSX
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-center text-[10px] font-semibold text-slate-600">
            SQL
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-center text-[10px] font-semibold text-slate-600">
            API
          </div>
        </div>
      </div>
    );
  }

  if (idx === 1) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
          {`"Show trend and top issues this week"`}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-end gap-1.5">
        <div className="h-5 w-3 rounded bg-cyan-300" />
        <div className="h-8 w-3 rounded bg-cyan-500" />
        <div className="h-12 w-3 rounded bg-blue-500" />
        <div className="h-7 w-3 rounded bg-blue-400" />
      </div>
      <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-cyan-700">
        <Sparkles className="h-3 w-3" />
        {lang === 'id' ? 'Insight siap pakai' : 'Insight ready'}
      </div>
    </div>
  );
};

const EricaHowItWorks: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];

  return (
    <section id="how-it-works" className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {c.howItWorks.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{c.howItWorks.subtitle}</p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {c.howItWorks.steps.map((step, idx) => {
            const Icon = ICONS[idx] ?? Database;
            return (
              <div
                key={step.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                    <Icon className="h-6 w-6 text-slate-900" />
                  </div>
                  <div className="text-sm font-semibold text-slate-400">
                    {(idx + 1).toString().padStart(2, '0')}
                  </div>
                </div>
                <div className="mt-4">
                  <StepVisual idx={idx} lang={lang} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EricaHowItWorks;
